import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import { toRaw } from '@vue/reactivity';
import { OJ_API_BASE } from '@core/utils/environment';
import { UserService } from './user.service';
import { initFallbackWorker } from './websocket.fallback';
import {
  WS_EVENT,
  type WsBaseRequestData,
  type WsEventType,
  buildWsUrl,
  isValidToken,
} from './websocket.helpers';

// ── 事件常量与事件类型（从 helpers 导入再 re-export，保持业务 import 路径不变）────
export { WS_EVENT, type WsEventType } from './websocket.helpers';

// ── handler 类型 ──────────────────────────────────────────────────────────────
export type WsConnectedHandler = (isReconnect: boolean) => void;
export type WsDisconnectedHandler = () => void;
export type WsResponseHandler<T = unknown> = (data: T) => void;
export type WsHandler =
  | WsConnectedHandler
  | WsDisconnectedHandler
  | WsResponseHandler<any>;

// ── 请求数据类型 ──────────────────────────────────────────────────────────────
export type UserCountRequestData = WsBaseRequestData;
export type UserWallRequestData = WsBaseRequestData;
export type UserRewardRequestData = WsBaseRequestData;
export type UserTimeRequestData = WsBaseRequestData;

// ── 响应数据类型 ──────────────────────────────────────────────────────────────
export interface UserCountResponseData {
  userCount: number;
}
export interface UserWallRecord {
  name: string;
  avatar: string; // 后端已修正字段名 avtar → avatar
}
export interface UserWallResponseData {
  records: UserWallRecord[];
}
export interface UserRewardRecord {
  name: string;
  avatar: string; // 后端已修正字段名 avtar → avatar
  goods: string;
  level: number;
}
export interface UserRewardResponseData {
  records: UserRewardRecord[];
}

// ── Worker 事件类型（仅内部使用）────────────────────────────────────────────────
type WorkerEvent =
  | { event: 'connected'; isReconnect: boolean }
  | { event: 'disconnected'; closeCode?: number; closeReason?: string }
  | { event: 'kicked'; closeCode?: number; closeReason?: string }
  | { event: 'authFailed'; closeCode?: number; closeReason?: string }
  | { event: 'message'; type: string; data: unknown };

// ── 服务本体 ─────────────────────────────────────────────────────────────────
@Injectable()
export class WebSocketService {
  @Inject(forwardRef(() => UserService))
  private userService!: UserService;

  private port: MessagePort | null = null;
  private handlers = new Map<WsEventType, Set<WsHandler>>();
  /** 主线程侧对 WS 连接状态的镜像，由 Worker 的 connected/disconnected 事件维护 */
  private _isConnected = false;

  // ── 公开 API ────────────────────────────────────────────────────────────────

  /**
   * 若连接已 OPEN，立即执行回调；否则等待下一次 CONNECTED 事件后执行一次。
   * 用于页面重新进入时跳过等待 CONNECTED 触发的问题。
   */
  public ready(callback: () => void): void {
    if (this._isConnected) {
      callback();
    } else {
      const onceHandler: WsConnectedHandler = () => {
        this.unsubscribe(WS_EVENT.CONNECTED, onceHandler);
        callback();
      };
      this.subscribe(WS_EVENT.CONNECTED, onceHandler);
    }
  }

  /** 订阅事件。首次订阅时自动建立 SharedWorker 连接。 */
  public subscribe(event: WsEventType, handler: WsHandler): void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);
    this.connect();
  }

  /** 取消订阅事件。不关闭 WebSocket 连接。 */
  public unsubscribe(event: WsEventType, handler: WsHandler): void {
    this.handlers.get(event)?.delete(handler);
  }

  /**
   * 销毁 WebSocket 连接和 SharedWorker/MessagePort。
   * 用于退出登录时主动关闭连接，防止残留旧 token 的 WebSocket 被新登录复用。
   */
  public destroy(): void {
    if (this.port) {
      this.port.postMessage({ cmd: 'disconnect' });
      this.port.close();
      this.port = null;
    }
    this._isConnected = false;
  }

  /** 发送"获取参赛人数"请求 */
  public sendUserCount(data: UserCountRequestData): void {
    this._send(WS_EVENT.USER_COUNT, data);
  }

  /** 发送"获取用户墙"请求 */
  public sendUserWall(data: UserWallRequestData): void {
    this._send(WS_EVENT.USER_WALL, data);
  }

  /** 发送"获取中奖用户/获谷弹幕"请求 */
  public sendUserReward(data: UserRewardRequestData): void {
    this._send(WS_EVENT.USER_REWARD, data);
  }

  /** 发送"上报答题时长"请求（无响应） */
  public sendUserTime(data: UserTimeRequestData): void {
    this._send('userTime', data);
  }

  // ── 私有方法 ────────────────────────────────────────────────────────────────

  /**
   * 确认登录后由 UserService 调用，发起裸连接建立（幂等）。
   * subscribe() 内部也调用本方法作为兜底，覆盖"subscribe 早于连接建立"的时序。
   */
  public connect(): void {
    const token = this.userService.token;
    if (!isValidToken(token)) {
      return;
    }

    const wsUrl = buildWsUrl(OJ_API_BASE, token);

    // 已初始化（任意路径）：直接发 connect 命令，Worker/Fallback 内部幂等
    if (this.port) {
      this.port.postMessage({ cmd: 'connect', wsUrl });
      return;
    }

    // 首次初始化：根据 SharedWorker 支持情况选择路径
    if (typeof SharedWorker !== 'undefined') {
      // SharedWorker 路径
      const sw = new SharedWorker('/ws-worker.js', {
        type: 'module',
        name: 'hetao-ws-worker',
      });
      this.port = sw.port;
    } else {
      // 降级路径：MessageChannel 模拟 SharedWorker 通信模型
      const ch = new MessageChannel();
      initFallbackWorker(ch.port2); // port2 → "假 Worker"
      this.port = ch.port1; // port1 → Service
    }

    // 两条路径共享后续逻辑——都只操作 MessagePort
    this.port.onmessage = (e: MessageEvent) =>
      this._onWorkerMessage(e.data as WorkerEvent);
    this.port.start();
    this.port.postMessage({ cmd: 'connect', wsUrl });
  }

  private _onWorkerMessage(msg: WorkerEvent): void {
    switch (msg.event) {
      case 'connected':
        this._isConnected = true;
        this._dispatch(WS_EVENT.CONNECTED, msg.isReconnect);
        break;
      case 'disconnected':
        this._isConnected = false;
        console.log(
          `[WebSocketService] disconnected — closeCode=${msg.closeCode}, closeReason="${msg.closeReason}"`,
        );
        this._dispatch(WS_EVENT.DISCONNECTED, undefined);
        break;
      case 'kicked':
        this._isConnected = false;
        console.log(
          `[WebSocketService] kicked — closeCode=${msg.closeCode}, closeReason="${msg.closeReason}"`,
        );
        this._dispatch(WS_EVENT.KICKED, undefined);
        break;
      case 'authFailed':
        this._isConnected = false;
        console.error(
          `[WebSocketService] 认证失败（token 失效等），已停止重连 — closeCode=${msg.closeCode}, closeReason="${msg.closeReason}"`,
        );
        this._dispatch(WS_EVENT.AUTH_FAILED, undefined);
        break;
      case 'message':
        this._dispatch(msg.type as WsEventType, msg.data);
        break;
    }
  }

  private _send(type: string, data: object): void {
    if (!this._isConnected) {
      return;
    }
    // toRaw() 剥离 Vue 响应式 Proxy，避免 postMessage 结构化克隆算法
    // 无法序列化 Proxy 而抛出 DataCloneError（比展开运算符更安全，支持嵌套对象）
    this.port?.postMessage({ cmd: 'send', type, data: toRaw(data) });
  }

  private _dispatch(event: WsEventType, payload: unknown): void {
    const set = this.handlers.get(event);
    if (!set) return;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    set.forEach((handler) => (handler as (p: unknown) => void)(payload));
  }
}
